import { lazy, Suspense, useEffect } from 'react';
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import { useLocation, useMatch, useParams } from 'react-router-dom';

import { rtaTheme } from '../theme';
import { useLoadAccessoryData, useLoadCountertopSkuData, useLoadCustomerDesigns, useLoadIslandSkuData, useLoadPromotionData } from '@context';
import { AppLoadingPanel, AppToastProvider, BottomDock, ConfirmCustomerCloseAlert, DeleteAllDraftsModal, DeleteCustomerBlueprintModal, DeleteCustomerDraftsModal, GeneratePresentationModal, GetNewCustomerDataModal, PushSubmissionModal, SaveAsDraftAlert, SavingOverlay, TopDock } from '@components';
import { CadArea } from '../components/cad-area';
import { useRecordRecentDesign } from '@hooks';
import { selectIsSaving, selectNavigation, setNavigation, useAppDispatch, useAppSelector } from '@state';

// dev-bridge is optional and only available in local dev environments
// DevBridge moved to AuthGate so it mounts on EVERY authenticated route, not just the CAD shell —
// the admin routes render DataManagerApp instead of App and had no bridge at all.
const DevBridgeMovedToAuthGate = import.meta.env.DEV
	? lazy(() => import('@rta/dev-bridge').then((m) => ({ default: m.DevBridge })).catch(() => ({ default: () => null })))
	: null;

import './app.css';
import { useLoadComponentData } from '../context/functions/load-component-data';

export const App = () => {
	const appNav = useAppSelector(selectNavigation);
	const isSaving = useAppSelector(selectIsSaving);
	const dispatch = useAppDispatch();

	const matchA = useMatch({ path: '/:type/*' });
	const params = useParams();
	const location = useLocation();

	useEffect(() => {
		console.log('app:params', params, matchA, location.search === '?draft');
		dispatch(setNavigation({
			mode: matchA?.params.type ?? 'r',
			params,
			draft: location.search === '?draft',
			reset: location.search === '?reset'
		}));
	}, [matchA?.params.type, params, location.search]);

	useLoadAccessoryData();

	useLoadComponentData();

	useLoadCountertopSkuData();

	useLoadIslandSkuData();

	useLoadPromotionData();

	useLoadCustomerDesigns({ appNav });

	useRecordRecentDesign();

	// console.log('render:App');
	return (<>
		<ChakraProvider theme={rtaTheme}>
			<AppToastProvider>
			<ColorModeScript />

			{/* application loading / bootstrapping */}
			<AppLoadingPanel/>

			{/* full-app lock during a save + its reload (see use-save-feature) */}
			{isSaving && <SavingOverlay/>}

			{/* application areas */}
			<TopDock/>
			<CadArea/>
			<BottomDock/>

			{/* application alerts */}
			<div>
				<ConfirmCustomerCloseAlert/>
				<SaveAsDraftAlert/>
			</div>

			{/* application modals */}
			<div>
				<GeneratePresentationModal/>
				<GetNewCustomerDataModal/>
				<PushSubmissionModal/>
				<DeleteCustomerDraftsModal/>
				<DeleteAllDraftsModal/>
				<DeleteCustomerBlueprintModal/>
			</div>

			</AppToastProvider>
		</ChakraProvider>
	</>);
};

